import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import useScrollNavigation from "../hooks/useScrollNavigation";
import CrisisNotice from "./CrisisNotice";
import SiteBanner from "./SiteBanner";
import { useSiteSettings } from "../hooks/useContent";
import { socialLinks } from "../data/content";
import logoWordmark from "../assets/brand/logo-wordmark.png";

const NAV_ID = "primary-navigation";
const SERVICES_MENU_ID = "services-submenu";

// Email-to-book offerings that live on their own pages (Services menu).
const SERVICE_PAGES = [
  { to: "/in-home-art-therapy", label: "In-Home Art Therapy" },
  { to: "/packages", label: "Packages & Bundles" },
  { to: "/groups-and-workshops", label: "Groups & Workshops" },
  { to: "/organizations", label: "Organizations" },
  { to: "/schools", label: "Schools" },
];

// Honour the OS "reduce motion" setting for JS-driven scrolling too.
function scrollBehavior() {
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesButtonRef = useRef(null);
  const servicesGroupRef = useRef(null);
  const location = useLocation();
  const { createNavigationHandler } = useScrollNavigation();
  const menuButtonRef = useRef(null);
  const social = socialLinks(useSiteSettings().social);

  useEffect(() => {
  // close mobile menu (and the Services submenu) on route change
  setMenuOpen(false);
  setServicesOpen(false);

  // if there's a hash (/#about or /#contact), smooth scroll
  if (location.hash) {
    const id = location.hash.replace("#", "");
    // wait a tick so the DOM is ready after route change
    setTimeout(() => {
      const el = document.getElementById(id);
      el && el.scrollIntoView({ behavior: scrollBehavior() });
    }, 0);
  } else if (location.pathname === "/") {
    // no hash and we're on home then reset scroll to top
    window.scrollTo({ top: 0, behavior: "instant" });
  }
}, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => {
      const navbar = document.getElementById("navbar");
      if (!navbar) return;
      if (window.scrollY > 10) navbar.classList.add("scrolled");
      else navbar.classList.remove("scrolled");
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Escape closes the mobile menu and returns focus to its toggle.
  // The Services submenu closes first, then the mobile menu.
  const onNavKeyDown = (e) => {
    if (e.key === "Escape" && servicesOpen) {
      setServicesOpen(false);
      servicesButtonRef.current && servicesButtonRef.current.focus();
      return;
    }
    if (e.key === "Escape" && menuOpen) {
      setMenuOpen(false);
      menuButtonRef.current && menuButtonRef.current.focus();
    }
  };

  // Close the Services submenu when keyboard focus moves outside it. A
  // null relatedTarget (e.g. Safari, which does not focus clicked links)
  // is left to the outside-click handler below.
  const onServicesBlur = (e) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) setServicesOpen(false);
  };

  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onPointerDown = (e) => {
      const group = servicesGroupRef.current;
      if (group && !group.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [servicesOpen]);

  const closeMenus = () => {
    setServicesOpen(false);
    setMenuOpen(false);
  };

  const backToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
    const top = document.getElementById("top");
    top && top.focus({ preventScroll: true });
  };

  // Move focus without touching location.hash (pages such as /policies
  // read the hash for their own state).
  const skipToMain = (e) => {
    e.preventDefault();
    const main = document.getElementById("main");
    if (main) {
      main.focus({ preventScroll: true });
      main.scrollIntoView({ behavior: scrollBehavior() });
    }
  };

  return (
    <>
      <a href="#main" id="top" className="skip-link" onClick={skipToMain}>Skip to main content</a>

      <nav id="navbar" className="navbar" aria-label="Main" onKeyDown={onNavKeyDown}>
  <div className="container nav-inner">
    <Link to="/" className="brand">
      <img className="brand-logo" src={logoWordmark} alt="Healing Hues" width="217" height="60" />
    </Link>

    <div id={NAV_ID} className={`nav-links ${menuOpen ? "active" : ""}`}>
      <NavLink to="/" end>Home</NavLink>

      {/* Scroll-to links that work from any page */}
      <a
        href="/#about"
        onClick={createNavigationHandler("about", () => setMenuOpen(false))}
      >
        About
      </a>

      <div className="nav-group" ref={servicesGroupRef} onBlur={onServicesBlur}>
        <button
          type="button"
          ref={servicesButtonRef}
          className="nav-disclosure"
          aria-expanded={servicesOpen}
          aria-controls={SERVICES_MENU_ID}
          onClick={() => setServicesOpen((open) => !open)}
        >
          Services<span className="nav-caret" aria-hidden="true">▾</span>
        </button>
        <ul id={SERVICES_MENU_ID} className="nav-submenu" hidden={!servicesOpen}>
          <li>
            <a href="/#services" onClick={createNavigationHandler("services", closeMenus)}>
              Online Sessions
            </a>
          </li>
          {SERVICE_PAGES.map((p) => (
            <li key={p.to}>
              <NavLink to={p.to}>{p.label}</NavLink>
            </li>
          ))}
        </ul>
      </div>
      <a
        href="/#contact"
        onClick={createNavigationHandler("contact", () => setMenuOpen(false))}
      >
        Contact
      </a>
      <NavLink to="/faqs">FAQs</NavLink>
      <NavLink to="/policies">Policies</NavLink>
      <NavLink to="/resources">Resources</NavLink>

      <Link className="btn-cta" to="/checkout">
        Book a Session
      </Link>
    </div>

    <button
      type="button"
      ref={menuButtonRef}
      className={`hamburger ${menuOpen ? "active" : ""}`}
      onClick={() => setMenuOpen((open) => !open)}
      aria-label={menuOpen ? "Close menu" : "Open menu"}
      aria-expanded={menuOpen}
      aria-controls={NAV_ID}
    >
      <span aria-hidden="true"></span><span aria-hidden="true"></span><span aria-hidden="true"></span>
    </button>
  </div>
</nav>


      <main id="main" tabIndex={-1}>
        <SiteBanner />
        {children}
      </main>

      <footer>
        <div className="container">
          <CrisisNotice variant="compact" />
          <div className="footer-row">
            <div>© {new Date().getFullYear()} Healing Hues. All rights reserved.</div>
            <nav className="footer-links" aria-label="Footer">
              <Link to="/about-more">About</Link>
              <Link to="/groups-and-workshops">Groups &amp; Workshops</Link>
              <Link to="/organizations">Organizations</Link>
              <Link to="/schools">Schools</Link>
              <Link to="/policies#privacy">Privacy</Link>
              <Link to="/policies#terms">Terms</Link>
              <Link to="/policies#refund">Cancellation &amp; Refunds</Link>
              {social.map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
              ))}
              <a href="#top" onClick={backToTop}>Back to top ↑</a>
            </nav>
          </div>
        </div>
      </footer>
    </>
  );
}
